import React, { useState } from 'react'
import { useSheetDragDismiss } from '../hooks/useSheetDragDismiss'
import SheetHeader from './SheetHeader'
import BoldCheckbox from './BoldCheckbox'
import AgreementItem from './AgreementItem'
import LegalDocViewer, { LegalDocType } from './LegalDocViewer'
import Gap from './Gap'
import SubmitButton from './SubmitButton'

interface TermsAgreementSheetProps {
  isOpen: boolean
  onClose: () => void
  /** 필수 약관 모두 동의 후 완료. 선택 항목(마케팅) 동의 여부 전달. */
  onComplete: (agree: { marketing: boolean }) => void
  /**
   * 드래그·바깥터치로 닫기 허용 여부(기본 true).
   * false면 필수 동의로 취급 — 드래그/오버레이로 닫히지 않고,
   * 헤더 뒤로가기(onClose) 또는 완료로만 벗어남.
   */
  dismissible?: boolean
}

/**
 * 회원가입 약관 동의 바텀시트 (닉네임 다음 단계).
 * 필수: 이용약관 / 위치기반 / 개인정보 / 만14세. 선택: 마케팅.
 */
const TermsAgreementSheet: React.FC<TermsAgreementSheetProps> = ({
  isOpen,
  onClose,
  onComplete,
  dismissible = true,
}) => {
  const [age14, setAge14] = useState(false)
  const [terms, setTerms] = useState(false)
  const [location, setLocation] = useState(false)
  const [privacy, setPrivacy] = useState(false)
  const [marketing, setMarketing] = useState(false)
  const [viewer, setViewer] = useState<LegalDocType | null>(null)

  const allRequired = age14 && terms && location && privacy
  const allChecked = allRequired && marketing

  const toggleAll = (v: boolean) => {
    setAge14(v)
    setTerms(v)
    setLocation(v)
    setPrivacy(v)
    setMarketing(v)
  }

  const { handleProps, sheetStyle } = useSheetDragDismiss({
    // 필수(dismissible=false)면 드래그로 닫히지 않고 원위치로 스냅
    onClose: dismissible ? onClose : () => {},
    enabled: isOpen,
  })

  if (!isOpen) return null

  return (
    <>
      {/* 오버레이 (필수면 바깥터치로 안 닫힘) */}
      <div
        className="fixed inset-0 bg-black bg-opacity-40 z-[1040]"
        onClick={dismissible ? onClose : undefined}
      />

      {/* 바텀 시트 */}
      <div className="fixed bottom-0 left-0 right-0 z-[1050] flex justify-center">
        <div className="app-container flex flex-col" style={sheetStyle}>
          <div className="bg-ivoryKey rounded-t-30px shadow-lg overflow-hidden flex flex-col w-full">
            {/* 드래그 핸들 — 필수 시트(dismissible=false)는 드래그 핸들러 없이 장식용(작동 안 함) */}
            <div
              className="flex flex-col w-full"
              {...(dismissible ? handleProps : {})}
            >
              <Gap height={24} />
              <div className="w-40px h-5px bg-d9d9d9 rounded-20px mx-auto"></div>
            </div>

            <Gap height={8} />

            {/* 헤더: 뒤로가기 + 이용약관 */}
            <SheetHeader
              title="이용약관"
              leftAction={
                <button
                  type="button"
                  onClick={onClose}
                  className="flex items-center justify-center cursor-pointer"
                >
                  <img
                    src="/images/back-arrow-icon.png"
                    alt="뒤로가기"
                    draggable={false}
                    className="w-48px h-48px object-contain object-left"
                  />
                </button>
              }
            />

            <Gap height={20} />

            {/* 전체 동의 */}
            <div
              className="px-20px flex items-center gap-16px cursor-pointer pl-8px py-10px mx-12px"
              onClick={() => toggleAll(!allChecked)}
            >
              <BoldCheckbox checked={allChecked} onChange={toggleAll} size={20} />
              <span className="font-pretendard font-bold text-16px leading-20px text-brown-80%-main">
                모든 약관에 동의합니다
              </span>
            </div>

            <Gap height={8} />

            {/* 개별 항목 */}
            <div className="px-20px flex flex-col gap-4px">
              <AgreementItem
                checked={terms}
                onChange={setTerms}
                label="필수"
                text="TASTEE 이용약관"
                onViewClick={() => setViewer('terms')}
              />
              <AgreementItem
                checked={location}
                onChange={setLocation}
                label="필수"
                text="위치기반서비스 이용약관"
                onViewClick={() => setViewer('location')}
              />
              <AgreementItem
                checked={privacy}
                onChange={setPrivacy}
                label="필수"
                text="개인정보 수집 및 이용 안내"
                onViewClick={() => setViewer('privacy')}
              />
              <AgreementItem
                checked={marketing}
                onChange={setMarketing}
                label="선택"
                text="마케팅 정보 수신 동의"
                onViewClick={() => setViewer('marketing')}
              />
              <AgreementItem
                checked={age14}
                onChange={setAge14}
                label="필수"
                text="만 14세 이상이에요"
              />
            </div>

            <Gap height={20} />

            {/* 완료 (앱 표준: 바닥 32px 고정) */}
            <div className="px-20px pb-32px">
              <SubmitButton
                text="완료"
                disabled={!allRequired}
                onSubmit={() => onComplete({ marketing })}
                onDisabled={() => {}}
              />
            </div>
          </div>
        </div>
      </div>

      {/* 약관 보기 — 앱 안 전체화면 뷰어(브라우저 이동 없음, 뒤로가기로 시트 복귀) */}
      <LegalDocViewer type={viewer} onClose={() => setViewer(null)} />
    </>
  )
}

export default TermsAgreementSheet
